import {customAlphabet} from 'nanoid'
import React, {useCallback, useEffect, useState} from 'react'
import styled from 'styled-components'
import SVGStepOutput from '../../../../../assets/icons/context-output-small.svg'
import SVGGoal from '../../../../../assets/icons/icon-goal-small.svg'
import SVGProfile from '../../../../../assets/icons/profile-small.svg'
import SmallSearchIcon from '../../../../../assets/icons/search-small.svg'
import SVGWait from '../../../../../assets/icons/timer.svg'
import SVGEvent from '../../../../../assets/icons/tomorrow-medium.svg'
import SVGVariable from '../../../../../assets/icons/variable-small.svg'
import {getCardTemplatesFromStep} from '../../../../../common/action-flows/get-card-templates-from-step'
import {ContextMappingType, UIElementType} from '../../../../../common/types'
import {useActionFlowAnalytics} from '../../../../../lib/analytics/action-flow-analytics'
import {mergeRefs} from '../../../../../lib/helpers'
import {useFocusFirstFormInput} from '../../../../../lib/hooks'
import {useVariables} from '../../../../../providers/variables/provider'
import {useActionFlowSampleContext} from '../../../hooks/use-action-flow-sample-context'
import {useSearchRegex} from '../../../../../hooks/use-search-regex'
import {useAFConfig} from '../../../state/Provider'
import {fontSmall, fontXSmall} from '../css'
import {useDropdownOptionAccessability} from '../custom-select/use-dropdown-option-accessability'
import {BuilderIcon, getIconNameForElement} from '../icon'
import {Switch} from '../switch'
import {
  ContextGroup,
  OptionType,
  ProfileContext,
  StepContext,
  getContextGroups
} from './get-context-groups'

const SearchBox = styled.form`
  border: 1px solid ${({theme}) => theme.colors.grey10};
  border-radius: 4px;
  padding: 7px 12px;
  display: flex;
  align-items: center;
  gap: 8px;
`

const StyledSearchIcon = styled(SmallSearchIcon)`
  color: ${({theme}) => theme.black};
`

const SearchInput = styled.input`
  border: none;
  margin: 0;
  padding: 0;
  overflow: hidden;
  flex-grow: 1;
  ${fontXSmall};
`

const Wrapper = styled.div`
  width: 440px;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 24px;
`
const Title = styled.h3<{color: 'plum' | 'green' | 'blue'}>`
  ${fontSmall}
  font-weight: 700;
  display: flex;
  gap: 12px;
  align-items: center;
  height: 32px;
  margin: 0;
  margin-bottom: -16px;
  > svg {
    background-color: ${p =>
      p.color === 'blue'
        ? p.theme.colors.blue4
        : p.color === 'plum'
        ? p.theme.colors.plum4
        : p.theme.colors.lime4};
    color: ${p =>
      p.color === 'blue'
        ? p.theme.colors.blue10
        : p.color === 'plum'
        ? p.theme.colors.plum10
        : p.theme.colors.lime10};
    width: 28px;
    height: 28px;
    border-radius: 4px;
    display: flex;
    padding: 6px;
    align-items: center;
  }
`
const Section = styled.div`
  display: flex;
  padding: 0;
  flex-direction: column;
  align-items: flex-start;
  align-self: stretch;
  border-radius: 4px;
  border: 1px solid ${p => p.theme.colors.grey6};
`
const SectionTitle = styled.h4`
  ${fontXSmall}
  margin: 0px;
  display: flex;
  align-items: center;
  gap: 12px;
  font-weight: 400;
  height: 36px;
  padding: 8px 12px;
  svg {
    color: ${p => p.theme.colors.blue11};
  }
`
const SectionIntro = styled.div`
  padding: 12px 16px;
  color: ${p => p.theme.colors.grey12};
  font-weight: 500;
  border-top: 1px solid ${p => p.theme.colors.grey4};
  background-color: ${p => p.theme.colors.grey2};
  align-self: stretch;
  ${fontSmall}
  overflow: hidden;
  text-overflow: ellipsis;
`

const SectionTitleWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-self: stretch;
  border-bottom: 1px solid ${p => p.theme.colors.grey4};
  margin: 8px;
  border: 1px solid ${p => p.theme.colors.grey4};
  border-radius: 4px;
`

const ItemType = styled.div`
  display: flex;
  align-items: center;
  color: ${p => p.theme.colors.grey11};
  font-family: 'atomic___RobotoMono';
  gap: 8px;
  justify-content: flex-end;
  ${fontSmall}
`
const SectionInner = styled.div`
  display: flex;
  align-self: stretch;
  flex-direction: column;
`

const Item = styled.div`
  display: flex;
  height: 32px;
  padding: 0px 16px;
  justify-content: space-between;
  align-items: center;
  align-self: stretch;
  ${fontSmall}
  cursor: pointer;
  .no-label {
    opacity: 0.5;
    font-style: italic;
  }
  &:hover,
  &:focus {
    background-color: ${p => p.theme.colors.grey4};
  }
  &:focus {
    outline: none;
  }
`

const SectionWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  &:last-of-type {
    margin-bottom: 24px;
  }
`

export type ContextSelection = {
  type: ContextMappingType
  id: string
  label: string
  path: string
  referenceId?: string
}

type ContextPickerProps = {
  stepId?: string
  environmentId: string
  onSelect: (option: ContextSelection) => void
  onClose: () => void
}

const optionTypes: OptionType[] = ['input', 'option', 'eventOption']

export const ContextPicker = React.forwardRef<
  HTMLDivElement,
  ContextPickerProps
>(({stepId, onSelect, environmentId, onClose}, ref) => {
  const config = useAFConfig()
  const {track} = useActionFlowAnalytics()
  const [groups, setGroups] = useState<ContextGroup[]>()
  const [search, setSearch] = useState('')
  const [showEvent, setShowEvent] = useState(false)

  const [predictedContext] = useActionFlowSampleContext({
    stepId,
    config,
    environmentId
  })

  useEffect(() => {
    track({
      event: 'Opened action flow insert context placeholder menu'
    })
  }, [track])

  useEffect(() => {
    if (predictedContext) {
      setGroups(getContextGroups(config, predictedContext))
    }
  }, [predictedContext, setGroups, config])

  const searchRegex = useSearchRegex(search)
  const variables = Object.entries(useVariables()).filter(([id]) =>
    searchRegex ? searchRegex.test(id) : true
  )

  const stepOutputs = groups
    ?.filter((g): g is StepContext => g.type === 'step')
    ?.reduce((mut_acc, group) => {
      // TS doesn't think there's enough overlap between the types here, so adding a simple cast of the only used parameter
      const options = (
        group.options as {type: OptionType; label?: string}[]
      ).filter(
        option =>
          (option.type !== 'eventOption' || showEvent) &&
          (searchRegex ? option.label && searchRegex.test(option.label) : true)
      )
      // @ts-ignore TS is concerned here we might be putting the wrong options
      // with the wrong group as StepContext is a combination of two types.
      // We know this isn't the case though, as we're filtering the options from the group.
      mut_acc.push({...group, options})
      return mut_acc
    }, [] as StepContext[])
    ?.filter(group => group.options.length)

  const profileOutputOptions = groups
    ?.find((g): g is ProfileContext => g.type === 'profile')
    ?.options?.filter(option =>
      searchRegex ? option.label && searchRegex.test(option.label) : true
    )

  const boldMatchedSearch = useCallback(
    (label: string) => {
      if (!search || !searchRegex) return label
      // Split the name into parts, and wrap the parts that match the search text <strong>
      const parts = label.split(searchRegex)

      return parts.map((part, index) =>
        searchRegex.test(part) ? <strong key={index}>{part}</strong> : part
      )
    },
    [search, searchRegex]
  )

  // Ensure options are keyboard navigable
  const [optionContainerRef] = useDropdownOptionAccessability<HTMLDivElement>({
    optionSelector: '.context-picker--item',
    focusFirstOnOpen: false, // otherwise we steal focus from the search input
    // we need to reattach listeners as the options change
    reAttachListenersId: JSON.stringify([
      profileOutputOptions,
      stepOutputs,
      variables
    ])
  })

  return (
    <Wrapper ref={mergeRefs([ref, optionContainerRef])}>
      <SearchFilter
        onChange={setSearch}
        value={search}
        onKeyDown={event => {
          // Focus the first option when the user presses the down arrow
          if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
            const firstOption = document.querySelector(
              '.context-picker--item'
            ) as HTMLElement
            if (firstOption) {
              firstOption.focus()
            }
          } else if (event.key === 'Escape') {
            event.stopPropagation()
            onClose()
          }
        }}
      />

      {profileOutputOptions?.length ? (
        <SectionWrapper>
          <Title color="green">
            <SVGProfile />
            Profile values
          </Title>
          <Section>
            <SectionInner>
              {[...profileOutputOptions]
                .sort((a, b) => a.name.localeCompare(b.name))
                .map(option => {
                  return (
                    <Item
                      className="context-picker--item"
                      tabIndex={0}
                      role={'button'}
                      key={option.path}
                      onClick={() =>
                        onSelect({
                          type: 'profile',
                          id: getMappingId(option.id),
                          label: option.name, // show the field name in the rendered pill
                          path: option.path
                        })
                      }
                    >
                      <span>
                        {option.label ? boldMatchedSearch(option.label) : ''}
                      </span>
                    </Item>
                  )
                })}
            </SectionInner>
          </Section>
        </SectionWrapper>
      ) : null}
      <SectionWrapper>
        <Title color="plum">
          <SVGStepOutput />
          Step outputs
          <div style={{marginLeft: 'auto', fontWeight: '400'}}>
            <Switch
              label="Include Event"
              checked={showEvent}
              onChange={setShowEvent}
            />
          </div>
        </Title>
        {stepOutputs?.map(group => {
          const step = group.step
          const template = Object.values(
            getCardTemplatesFromStep(config, step) ?? {}
          )?.[0]
          return (
            <Section key={step.id}>
              <SectionTitleWrap>
                <SectionTitle>
                  {step.type === 'event-listener' ? (
                    <>
                      <SVGEvent /> Listen for card event
                    </>
                  ) : step.type === 'send-request' ? (
                    <>
                      <SVGEvent /> Send request
                    </>
                  ) : step.type === 'wait' ? (
                    <>
                      <SVGWait /> Wait
                    </>
                  ) : step.type === 'goal' ? (
                    <>
                      <SVGGoal /> Goal
                    </>
                  ) : null}
                </SectionTitle>
                {template &&
                step.type === 'event-listener' &&
                step.properties.condition.type === 'card' ? (
                  <SectionIntro>
                    <strong>{template.metadata.cardName}</strong>
                    {' has been '}
                    {step.properties.condition.eventNames[0].replace(
                      /^card-/,
                      ''
                    )}
                  </SectionIntro>
                ) : step.type === 'send-request' &&
                  step.properties.output.type === 'simple' &&
                  step.properties.output.url.type === 'static' ? (
                  <SectionIntro>
                    <strong>{String(step.properties.output.url.value)}</strong>
                  </SectionIntro>
                ) : (step.type === 'wait' || step.type === 'goal') &&
                  step.name ? (
                  <SectionIntro>
                    <strong>{step.name}</strong>
                  </SectionIntro>
                ) : null}
              </SectionTitleWrap>
              <SectionInner>
                {[...group.options]
                  .sort((a, b) => {
                    const typeOrderA = optionTypes.indexOf(a.type)
                    const typeOrderB = optionTypes.indexOf(b.type)

                    if (typeOrderA !== typeOrderB) {
                      return typeOrderA - typeOrderB
                    } else {
                      return a.name.localeCompare(b.name)
                    }
                  })
                  .map(option => {
                    return (
                      <Item
                        role={'button'}
                        key={option.path}
                        className="context-picker--item"
                        tabIndex={0}
                        onClick={() =>
                          onSelect({
                            type: 'context',
                            id: getMappingId(option.id),
                            referenceId:
                              option.type === 'input'
                                ? option.inputId
                                : undefined,
                            label: option.name, // show the field name in the rendered pill
                            path: option.path
                          })
                        }
                      >
                        {option.label ? (
                          <span>{boldMatchedSearch(option.label)}</span>
                        ) : (
                          <span className="no-label">no label</span>
                        )}

                        <ItemType>
                          {option.type === 'input' ? (
                            <>
                              {option.inputType === 'submitButton'
                                ? 'additional payload' // submit buttons don't have a name. But show any additional payload
                                : option.name}
                              {option.inputType ? (
                                <FieldIcon type={option.inputType} />
                              ) : null}
                            </>
                          ) : (
                            option.valueType
                          )}
                        </ItemType>
                      </Item>
                    )
                  })}
              </SectionInner>
            </Section>
          )
        })}
      </SectionWrapper>
      {Object.keys(variables).length ? (
        <SectionWrapper>
          <Title color="blue">
            <SVGVariable width={28} height={28} /> Variables
          </Title>
          <Section>
            <SectionInner>
              {[...variables]
                .sort((a, b) => a[0].localeCompare(b[0]))
                .map(([id, variable]) => {
                  return (
                    <Item
                      role={'button'}
                      key={id}
                      className="context-picker--item"
                      tabIndex={0}
                      onClick={() =>
                        onSelect({
                          type: 'variable',
                          id,
                          label: id, // show the field name in the rendered pill
                          path: id
                        })
                      }
                    >
                      <span>{boldMatchedSearch(id)}</span>
                      <ItemType>{variable.type}</ItemType>
                    </Item>
                  )
                })}
            </SectionInner>
          </Section>
        </SectionWrapper>
      ) : null}
    </Wrapper>
  )
})

const SearchFilter = ({
  onChange,
  value,
  onKeyDown
}: {
  onChange: (text: string) => void
  value: string
  onKeyDown?: React.KeyboardEventHandler<HTMLElement>
}) => {
  const ref = useFocusFirstFormInput()
  return (
    <SearchBox onSubmit={e => e.preventDefault()} ref={ref}>
      <StyledSearchIcon />
      <SearchInput
        id="search-context-picker"
        onChange={e => onChange(e.target.value)}
        placeholder="Search variables, profile fields or step outputs..."
        type="search"
        value={value}
        onKeyDown={onKeyDown}
      />
    </SearchBox>
  )
}

const getMappingId = (label: string) =>
  `${label.trim().toLowerCase().replace(/[^\w]/g, '_')}_${customAlphabet(
    'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXY0123456789'
  )(4)}`

const FieldIcon = ({type}: {type: UIElementType | string}) => {
  return <BuilderIcon iconName={getIconNameForElement(type)} />
}
